import { useEffect, useMemo, useRef, useState } from "react";
import { Search, ChevronDown, X, Check } from "lucide-react";

/**
 * SearchableMultiSelect — Iter 372.p2
 *
 * Multi-select cousin of SearchableSelect. Same typed-search combobox
 * UX, but the value is an ARRAY of selected ids and clicking a row
 * toggles it in/out of the selection instead of dismissing the
 * popover. Selected items render as removable chips on the trigger.
 *
 * Props:
 *   - value: string[] (selected option ids)
 *   - onChange: (ids: string[]) => void
 *   - options: [{ id, label, sublabel?, searchTerms?: string[] }]
 *   - placeholder?: string
 *   - allLabel?: string   — label to show on the trigger when no ids selected (default "All")
 *   - disabled?: boolean
 *   - testId?: string
 *   - emptyHint?: string
 *
 * Kept intentionally lean: no "create new" flow, no sub-labels
 * required — those are single-select concerns.
 */
export default function SearchableMultiSelect({
  value,
  onChange,
  options = [],
  placeholder = "Search…",
  allLabel = "All",
  disabled = false,
  testId,
  emptyHint = "No matches.",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);

  const selectedIds = useMemo(
    () => (Array.isArray(value) ? value.filter(Boolean) : []),
    [value],
  );
  const selectedOptions = useMemo(
    () => selectedIds.map((id) => options.find((o) => o.id === id)).filter(Boolean),
    [selectedIds, options],
  );

  const filtered = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const hay = [
        o.label,
        o.sublabel,
        ...(o.searchTerms || []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const toggle = (id) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const clearAll = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      <div
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((o) => !o);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
        className={`hv-input w-full text-xs flex items-center gap-1.5 min-h-[36px] py-1 ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
        data-testid={testId}
        data-open={open ? "true" : "false"}
      >
        <span className="flex-1 flex flex-wrap gap-1 items-center min-w-0 text-left">
          {selectedOptions.length === 0 ? (
            <span className="text-[#647087]">{allLabel}</span>
          ) : (
            selectedOptions.map((opt) => (
              <span
                key={opt.id}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#1FB6C1]/15 text-[#a0e6ea] text-[10px] border border-[#1FB6C1]/25 max-w-[140px]"
                onClick={(e) => e.stopPropagation()}
                data-testid={`${testId}-chip-${opt.id}`}
              >
                <span className="truncate">{opt.label}</span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggle(opt.id); }}
                  className="hover:text-white shrink-0"
                  aria-label={`Remove ${opt.label}`}
                  data-testid={`${testId}-chip-remove-${opt.id}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))
          )}
        </span>
        {selectedIds.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="text-[#647087] hover:text-white shrink-0"
            aria-label="Clear selection"
            data-testid={`${testId}-clear`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
        <ChevronDown className={`w-3.5 h-3.5 text-[#647087] shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </div>

      {open && (
        <div
          className="absolute z-30 mt-1 w-full max-h-72 overflow-hidden rounded-md border border-white/10 bg-[#121a28] shadow-xl"
          data-testid={`${testId}-popover`}
        >
          <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-white/10">
            <Search className="w-3.5 h-3.5 text-[#647087]" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder}
              className="flex-1 bg-transparent outline-none text-xs text-white placeholder:text-[#647087]"
              data-testid={`${testId}-search`}
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-xs text-[#647087] italic">{emptyHint}</div>
            ) : (
              filtered.map((opt) => {
                const isSel = selectedIds.includes(opt.id);
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => toggle(opt.id)}
                    className={`w-full text-left px-2 py-1.5 text-xs flex items-center gap-2 hover:bg-white/[0.04] ${isSel ? "bg-[#1FB6C1]/10 text-[#a0e6ea]" : "text-white/85"}`}
                    data-testid={`${testId}-opt-${opt.id}`}
                  >
                    <span className="w-3.5 h-3.5 inline-flex items-center justify-center rounded border border-white/20">
                      {isSel && <Check className="w-3 h-3" />}
                    </span>
                    <span className="flex-1 truncate">{opt.label}</span>
                    {opt.sublabel && (
                      <span className="text-[10px] text-[#647087] truncate max-w-[110px]">{opt.sublabel}</span>
                    )}
                  </button>
                );
              })
            )}
          </div>
          <div className="border-t border-white/10 px-2 py-1.5 flex items-center justify-between text-[10px] text-[#647087]">
            <span>{selectedIds.length} selected</span>
            <button
              type="button"
              onClick={() => { setOpen(false); setQuery(""); }}
              className="hover:text-white"
              data-testid={`${testId}-done`}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
